import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSmartBack } from "../hooks/smartBack";
import InfiniteScroll from "react-infinite-scroll-component";
import { makeStyles, shorthands, Spinner, mergeClasses } from "@fluentui/react-components";
import {
  ArrowLeft20Regular,
  TextboxAlignBottomRotate9020Regular,
  Search20Regular,
  Dismiss16Regular,
  CheckmarkCircle20Regular,
  Warning20Regular,
  ChevronRight20Regular,
  Building20Regular,
  Person20Regular,
  ArrowUndo20Regular,
  Save20Regular,
  ArrowExportUp20Regular,
  Eraser20Regular,
} from "@fluentui/react-icons";
import { BRAND } from "../components/risikoanalyseStep/tokens";
import { useAuth } from "../context/AuthContext";
import { CleanupContactPreview } from "../components/CleanupContactPreview";
import { CleanupContactEditPanel } from "../components/CleanupContactEditPanel";
import { displayContactName } from "../utils/contactDisplay";
import {
  listNamenszusatzCleanup,
  fetchNamenszusatzCounts,
  fetchNamenszusatzTeams,
  fetchNamenszusatzRow,
  saveNamenszusatz,
  markNamenszusatzPasstSo,
  reopenNamenszusatz,
  type NamenszusatzRow,
  type NamenszusatzStatus,
} from "../services/namenszusatzCleanupService";

const PAGE_SIZE = 30;

const STATUS_COLORS: Record<string, { bg: string; fg: string }> = {
  Kunde: { bg: "#e7f6ec", fg: "#1b5e20" },
  Interessent: { bg: "#f0e6f2", fg: "#4d2051" },
  Altkunde: { bg: "#fdf3d6", fg: "#8a6d00" },
  "Verantwortung fürs Unternehmen": { bg: "#e3eefb", fg: "#1c4f86" },
  // Alter Name desselben Status (bis 29.09.2026).
  "Mensch fürs Unternehmen": { bg: "#e3eefb", fg: "#1c4f86" },
  Haushaltsmitglied: { bg: "#e4f3f1", fg: "#13675a" },
};
const statusChipStyle = (s: string | null | undefined): React.CSSProperties => {
  const c = (s && STATUS_COLORS[s]) || { bg: "#ececec", fg: "#5b5560" };
  return { backgroundColor: c.bg, color: c.fg };
};

/** Die drei Namensfelder im Bearbeitungszustand einer Karte. */
interface NameForm {
  lastname: string;
  firstName: string;
  suffix: string;
}
const formOf = (r: NamenszusatzRow): NameForm => ({
  lastname: r.lastname ?? "",
  firstName: r.firstName ?? "",
  suffix: r.suffix ?? "",
});
const isDirty = (r: NamenszusatzRow, f: NameForm): boolean => {
  const n = (s: string | null | undefined) => (s ?? "").trim();
  return (
    n(f.lastname) !== n(r.lastname) ||
    n(f.firstName) !== n(r.firstName) ||
    n(f.suffix) !== n(r.suffix)
  );
};

/**
 * Zusatz an den Namen anhängen — nur eine VORBELEGUNG des Formulars, gespeichert
 * wird erst mit dem Speichern-Knopf.
 *
 * Endet der Name auf einem Trennstrich („AGGK Aachener Glas-"), wird der Zusatz
 * ohne Leerzeichen angehängt; der Bindestrich bleibt bewusst stehen, weil nur ein
 * Mensch entscheiden kann, ob er zum Wort gehört („Glas-verarbeitung" vs.
 * „Glasverarbeitung"). Sonst wird mit Leerzeichen verbunden.
 */
const appendSuffix = (f: NameForm): NameForm => {
  const base = f.lastname.trim();
  const add = f.suffix.trim();
  if (!add) return f;
  const joined = base.endsWith("-") ? `${base}${add}` : [base, add].filter(Boolean).join(" ");
  return { ...f, lastname: joined, suffix: "" };
};

const useStyles = makeStyles({
  root: {
    paddingTop: "24px",
    paddingBottom: "24px",
    paddingLeft: "32px",
    paddingRight: "32px",
    fontFamily: BRAND.fontPrimary,
    color: BRAND.ink,
    maxWidth: "1100px",
    marginLeft: "auto",
    marginRight: "auto",
    display: "flex",
    flexDirection: "column",
    rowGap: "16px",
  },
  backBtn: {
    alignSelf: "flex-start",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.padding("6px", "12px"),
    backgroundColor: "transparent",
    ...shorthands.border("1px", "solid", "transparent"),
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    cursor: "pointer",
    ...shorthands.borderRadius("6px"),
    ":hover": { backgroundColor: BRAND.bg },
  },
  bar: {
    backgroundColor: BRAND.purple,
    backgroundImage: `linear-gradient(135deg, ${BRAND.purple} 0%, ${BRAND.ink} 100%)`,
    color: BRAND.white,
    ...shorthands.padding("20px", "24px"),
    ...shorthands.borderRadius("10px"),
    boxShadow: "0 12px 24px rgba(45,20,43,0.18)",
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  titleRow: { display: "flex", alignItems: "center", columnGap: "12px" },
  titleIcon: {
    width: "40px",
    height: "40px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.1)",
    color: BRAND.gold,
    ...shorthands.borderRadius("999px"),
  },
  title: {
    margin: 0,
    fontFamily: BRAND.fontAccent,
    fontSize: "clamp(1.4rem, 3.4vw, 1.9rem)",
    color: BRAND.gold,
    flexGrow: 1,
  },
  goldLine: {
    height: "3px",
    backgroundColor: BRAND.gold,
    ...shorthands.borderRadius("999px"),
    width: "60px",
  },
  subtitle: { color: "rgba(255,255,255,0.85)", fontSize: "0.9rem" },
  filterBar: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    flexWrap: "wrap",
  },
  pill: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    ...shorthands.padding("6px", "12px"),
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("999px"),
    ":hover": { backgroundColor: BRAND.bg },
  },
  pillActive: {
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    ...shorthands.borderColor(BRAND.purple),
    ":hover": { backgroundColor: BRAND.purple, color: BRAND.white },
  },
  select: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    ...shorthands.padding("6px", "10px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    minWidth: "180px",
  },
  searchRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    flexGrow: 1,
    minWidth: "240px",
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    ...shorthands.padding("6px", "12px"),
  },
  searchInput: {
    flexGrow: 1,
    appearance: "none",
    ...shorthands.border("0", "solid", "transparent"),
    backgroundColor: "transparent",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    outlineStyle: "none",
  },
  clearBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    ...shorthands.padding("4px"),
    ...shorthands.border("none"),
    backgroundColor: "transparent",
    color: BRAND.muted,
    cursor: "pointer",
    ...shorthands.borderRadius("999px"),
    ":hover": { backgroundColor: BRAND.bg, color: BRAND.ink },
  },

  card: {
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("12px"),
    ...shorthands.padding("16px", "18px"),
    marginBottom: "12px",
    boxShadow: "0 1px 2px rgba(45,20,43,0.04), 0 6px 20px rgba(45,20,43,0.05)",
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
  },
  headRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    flexWrap: "wrap",
  },
  headIcon: {
    width: "34px",
    height: "34px",
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f0e6f2",
    color: BRAND.purple,
    ...shorthands.borderRadius("999px"),
  },
  headName: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.1rem",
    color: BRAND.ink,
  },
  chip: {
    fontSize: "0.72rem",
    fontWeight: 600,
    ...shorthands.padding("2px", "9px"),
    ...shorthands.borderRadius("999px"),
    whiteSpace: "nowrap",
  },
  metaChip: {
    fontSize: "0.75rem",
    color: BRAND.muted,
    whiteSpace: "nowrap",
  },
  fieldRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr) minmax(0, 2fr)",
    columnGap: "10px",
    rowGap: "10px",
    "@media (max-width: 720px)": { gridTemplateColumns: "minmax(0, 1fr)" },
  },
  field: { display: "flex", flexDirection: "column", rowGap: "4px", minWidth: 0 },
  label: {
    fontSize: "0.68rem",
    fontWeight: 600,
    color: BRAND.muted,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  input: {
    ...shorthands.padding("8px", "10px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    color: BRAND.ink,
    backgroundColor: "#fff",
    outlineStyle: "none",
    ":focus": { ...shorthands.borderColor(BRAND.purple) },
    ":disabled": { backgroundColor: "#f7f5f8", color: BRAND.muted },
  },
  inputSuffix: { backgroundColor: "#fffdf5" },
  previewLine: {
    display: "flex",
    alignItems: "baseline",
    columnGap: "8px",
    flexWrap: "wrap",
    fontSize: "0.85rem",
  },
  previewLabel: { color: BRAND.muted },
  previewValue: { fontWeight: 600, color: BRAND.ink },
  hint: { fontSize: "0.78rem", color: BRAND.muted },
  quickRow: { display: "flex", columnGap: "8px", flexWrap: "wrap" },
  quickBtn: {
    appearance: "none",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.padding("5px", "11px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("999px"),
    backgroundColor: BRAND.white,
    color: BRAND.purple,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    fontWeight: 600,
    ":hover": { backgroundColor: "#f0e6f2", ...shorthands.borderColor(BRAND.purple) },
    ":disabled": { opacity: 0.45, cursor: "not-allowed" },
  },
  infoToggle: {
    appearance: "none",
    cursor: "pointer",
    alignSelf: "flex-start",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "4px",
    ...shorthands.border("none"),
    ...shorthands.padding("2px", "0"),
    backgroundColor: "transparent",
    color: BRAND.purple,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.82rem",
    fontWeight: 600,
  },
  infoChevron: { transitionProperty: "transform", transitionDuration: "120ms" },
  infoChevronOpen: { transform: "rotate(90deg)" },
  infoPanel: {
    ...shorthands.padding("12px"),
    backgroundColor: "#faf8fb",
    ...shorthands.border("1px", "solid", "#ECE7F0"),
    ...shorthands.borderRadius("10px"),
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  actionRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    flexWrap: "wrap",
    ...shorthands.borderTop("1px", "solid", BRAND.borderDashed),
    paddingTop: "12px",
  },
  primaryBtn: {
    appearance: "none",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.padding("8px", "16px"),
    ...shorthands.border("1px", "solid", BRAND.purple),
    ...shorthands.borderRadius("8px"),
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.87rem",
    fontWeight: 600,
    ":hover": { backgroundColor: BRAND.ink },
    ":disabled": { opacity: 0.45, cursor: "not-allowed", backgroundColor: BRAND.purple },
  },
  secondaryBtn: {
    appearance: "none",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.padding("8px", "16px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.87rem",
    fontWeight: 600,
    ":hover": { backgroundColor: BRAND.bg, ...shorthands.borderColor(BRAND.purple) },
    ":disabled": { opacity: 0.45, cursor: "not-allowed" },
  },
  rowError: { color: "#8b1c1c", fontSize: "0.82rem" },
  rowOk: {
    color: "#1b5e20",
    fontSize: "0.82rem",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
  },
  empty: {
    color: BRAND.muted,
    fontStyle: "italic",
    paddingTop: "24px",
    paddingBottom: "24px",
    textAlign: "center",
  },
  loadMore: { textAlign: "center", color: BRAND.muted, ...shorthands.padding("12px") },
  errorBox: {
    backgroundColor: "#fdecec",
    color: "#8b1c1c",
    ...shorthands.padding("10px", "14px"),
    ...shorthands.border("1px", "solid", "#f5b3b3"),
    ...shorthands.borderRadius("8px"),
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
  },
});

const NamenszusatzCleanupPage = () => {
  const styles = useStyles();
  const goBack = useSmartBack("/datenbereinigung");
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("contacts:update");

  const [status, setStatus] = useState<NamenszusatzStatus>("open");
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  // Verantwortlich-Filter wie in den übrigen Bereinigungen: "" = alle,
  // "none" = ohne Verantwortlichkeit, sonst die BO-Team-Id.
  const [teamId, setTeamId] = useState("");
  const [teams, setTeams] = useState<Array<{ id: string; name: string | null }>>(
    [],
  );
  const [rows, setRows] = useState<NamenszusatzRow[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<{ open: number; erledigt: number }>({
    open: 0,
    erledigt: 0,
  });
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Bearbeitungszustand je Karte — der Server bleibt die Wahrheit, `forms` hält
  // nur die noch nicht gespeicherten Eingaben.
  const [forms, setForms] = useState<Record<number, NameForm>>({});
  const [infoOpen, setInfoOpen] = useState<Record<number, boolean>>({});
  const [busy, setBusy] = useState<Record<number, boolean>>({});
  const [rowErr, setRowErr] = useState<Record<number, string>>({});
  // Kurze Bestätigung nach einer Aktion — die Karte verschwindet dabei aus der
  // Liste, ohne Rückmeldung bliebe unklar, ob etwas passiert ist.
  const [flash, setFlash] = useState<string | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showFlash = useCallback((text: string) => {
    setFlash(text);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(null), 4000);
  }, []);
  useEffect(
    () => () => {
      if (flashTimer.current) clearTimeout(flashTimer.current);
    },
    [],
  );

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const ac = new AbortController();
    void fetchNamenszusatzTeams(ac.signal)
      .then(setTeams)
      .catch(() => {
        /* ohne Teams entfällt nur das Filter-Dropdown */
      });
    return () => ac.abort();
  }, []);

  const loadCounts = useCallback((team: string) => {
    void fetchNamenszusatzCounts(team || undefined)
      .then(setCounts)
      .catch(() => {
        /* Zähler sind Beiwerk — Fehler still schlucken */
      });
  }, []);

  const load = useCallback(
    async (
      st: NamenszusatzStatus,
      query: string,
      team: string,
      offset: number,
    ) => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      if (offset === 0) setLoading(true);
      try {
        const { rows: page, total: tot } = await listNamenszusatzCleanup(
          st,
          query || undefined,
          team || undefined,
          PAGE_SIZE,
          offset,
          ac.signal,
        );
        setRows((prev) => (offset === 0 ? page : [...prev, ...page]));
        setForms((prev) => {
          const next = offset === 0 ? {} : { ...prev };
          for (const r of page) if (!next[r.id]) next[r.id] = formOf(r);
          return next;
        });
        setTotal(tot);
        setHasMore(offset + page.length < tot);
        setError(null);
      } catch (e) {
        if (e instanceof DOMException && e.name === "AbortError") return;
        setError(e instanceof Error ? e.message : "Laden fehlgeschlagen");
        setHasMore(false);
      } finally {
        if (offset === 0) setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    void load(status, qDebounced, teamId, 0);
    loadCounts(teamId);
    return () => abortRef.current?.abort();
  }, [status, qDebounced, teamId, load, loadCounts]);

  const fetchMore = useCallback(() => {
    void load(status, qDebounced, teamId, rows.length);
  }, [load, status, qDebounced, teamId, rows.length]);

  const setForm = (id: number, patch: Partial<NameForm>) =>
    setForms((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  /** Zeile aus der Liste nehmen (sie gehört nach der Aktion in den anderen Reiter). */
  const dropRow = (id: number) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
    setTotal((t) => Math.max(0, t - 1));
    loadCounts(teamId);
  };

  const runRowAction = async (
    id: number,
    fn: () => Promise<unknown>,
    okText: string,
  ) => {
    setBusy((b) => ({ ...b, [id]: true }));
    setRowErr((m) => {
      const { [id]: _drop, ...rest } = m;
      return rest;
    });
    try {
      await fn();
      dropRow(id);
      showFlash(okText);
    } catch (e) {
      setRowErr((m) => ({
        ...m,
        [id]: e instanceof Error ? e.message : "Aktion fehlgeschlagen",
      }));
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  /**
   * Das Stammdaten-Panel speichert eigenständig (PUT /contact/:id) und kann dabei
   * denselben Namen anfassen. Beim Zuklappen wird die Zeile deshalb neu geladen —
   * unbestätigte Eingaben im Namensblock bleiben aber erhalten (nur ein
   * unveränderter Block wird überschrieben).
   */
  const toggleInfo = (id: number) => {
    const wasOpen = !!infoOpen[id];
    setInfoOpen((prev) => ({ ...prev, [id]: !wasOpen }));
    if (!wasOpen) return;
    void fetchNamenszusatzRow(id)
      .then((fresh) => {
        setRows((prev) => prev.map((r) => (r.id === id ? fresh : r)));
        setForms((prev) => {
          const cur = prev[id];
          const old = rows.find((r) => r.id === id);
          if (cur && old && isDirty(old, cur)) return prev; // Eingaben nicht wegwerfen
          return { ...prev, [id]: formOf(fresh) };
        });
      })
      .catch(() => {
        /* Aktualisieren ist Komfort — der Nutzer sieht weiter den alten Stand */
      });
  };

  const renderCard = (r: NamenszusatzRow) => {
    const f = forms[r.id] ?? formOf(r);
    const dirty = isDirty(r, f);
    const isFirma = r.contactType === "Firma";
    const working = !!busy[r.id];
    const err = rowErr[r.id];
    const vorschau =
      displayContactName({
        lastname: f.lastname,
        firstName: f.firstName,
        suffix: f.suffix,
        type: r.contactType,
      }) || "—";
    const bisher = displayContactName({ ...r, type: r.contactType }) || "—";

    return (
      <div key={r.id} className={styles.card}>
        <div className={styles.headRow}>
          <span className={styles.headIcon}>
            {isFirma ? <Building20Regular /> : <Person20Regular />}
          </span>
          <span className={styles.headName}>{bisher}</span>
          <span className={styles.chip} style={statusChipStyle(r.statusLabel)}>
            {r.statusLabel ?? "ohne Status"}
          </span>
          <span className={styles.chip} style={{ backgroundColor: BRAND.bg, color: BRAND.muted }}>
            {r.contactType ?? "ohne Typ"}
          </span>
          {r.kundennummer && (
            <span className={styles.metaChip}>Kd.-Nr. {r.kundennummer}</span>
          )}
          {r.contractCount > 0 && (
            <span className={styles.metaChip}>
              {r.contractCount} {r.contractCount === 1 ? "Vertrag" : "Verträge"}
            </span>
          )}
          {r.verantwortlich && (
            <span className={styles.metaChip}>· {r.verantwortlich}</span>
          )}
          {r.reviewedAt && (
            <span className={styles.chip} style={{ backgroundColor: "#e6f4ec", color: "#1b7f4d" }}>
              geprüft
            </span>
          )}
        </div>

        <div className={styles.fieldRow}>
          <label className={styles.field}>
            <span className={styles.label}>{isFirma ? "Firmenname" : "Name"}</span>
            <input
              className={styles.input}
              value={f.lastname}
              maxLength={255}
              disabled={!canWrite || working}
              autoComplete="off"
              onChange={(e) => setForm(r.id, { lastname: e.target.value })}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Vorname</span>
            <input
              className={styles.input}
              value={f.firstName}
              maxLength={45}
              disabled={!canWrite || working}
              autoComplete="off"
              onChange={(e) => setForm(r.id, { firstName: e.target.value })}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Namenszusatz</span>
            <input
              className={mergeClasses(styles.input, styles.inputSuffix)}
              value={f.suffix}
              maxLength={255}
              disabled={!canWrite || working}
              autoComplete="off"
              onChange={(e) => setForm(r.id, { suffix: e.target.value })}
            />
          </label>
        </div>

        <div className={styles.previewLine}>
          <span className={styles.previewLabel}>Wird angezeigt als:</span>
          <span className={styles.previewValue}>{vorschau}</span>
        </div>
        {!isFirma && f.suffix.trim() && (
          <span className={styles.hint}>
            Bei Personen wird der Namenszusatz nirgends angezeigt — er gehört
            entweder in den Namen oder ist zu löschen.
          </span>
        )}

        {canWrite && (
          <div className={styles.quickRow}>
            <button
              type="button"
              className={styles.quickBtn}
              disabled={!f.suffix.trim() || working}
              onClick={() => setForm(r.id, appendSuffix(f))}
            >
              <ArrowExportUp20Regular style={{ fontSize: 14 }} /> Zusatz an den Namen anhängen
            </button>
            <button
              type="button"
              className={styles.quickBtn}
              disabled={!f.suffix.trim() || working}
              onClick={() => setForm(r.id, { suffix: "" })}
            >
              <Eraser20Regular style={{ fontSize: 14 }} /> Zusatz löschen
            </button>
            {dirty && (
              <button
                type="button"
                className={styles.quickBtn}
                disabled={working}
                onClick={() => setForm(r.id, formOf(r))}
              >
                <ArrowUndo20Regular style={{ fontSize: 14 }} /> Zurücksetzen
              </button>
            )}
          </div>
        )}

        <CleanupContactPreview info={r} />

        <button
          type="button"
          className={styles.infoToggle}
          onClick={() => toggleInfo(r.id)}
          aria-expanded={!!infoOpen[r.id]}
        >
          <ChevronRight20Regular
            className={mergeClasses(styles.infoChevron, infoOpen[r.id] && styles.infoChevronOpen)}
          />
          Bisherige Informationen
        </button>
        {infoOpen[r.id] && (
          <div className={styles.infoPanel}>
            <span className={styles.hint}>
              Name, Vorname und Namenszusatz oben bearbeiten — hier stehen die
              übrigen Stammdaten mit eigenem Speichern-Knopf.
            </span>
            <CleanupContactEditPanel contactId={r.id} />
          </div>
        )}

        <div className={styles.actionRow}>
          {status === "erledigt" ? (
            <button
              type="button"
              className={styles.secondaryBtn}
              disabled={!canWrite || working}
              onClick={() =>
                void runRowAction(
                  r.id,
                  () => reopenNamenszusatz(r.id),
                  `„${bisher}“ ist wieder offen.`,
                )
              }
            >
              <ArrowUndo20Regular style={{ fontSize: 16 }} /> Wieder öffnen
            </button>
          ) : (
            <>
              <button
                type="button"
                className={styles.primaryBtn}
                disabled={!canWrite || working || !dirty}
                onClick={() =>
                  void runRowAction(
                    r.id,
                    () =>
                      saveNamenszusatz(r.id, {
                        lastname: f.lastname.trim() || null,
                        firstName: f.firstName.trim() || null,
                        suffix: f.suffix.trim() || null,
                      }),
                    `Gespeichert: „${vorschau}“`,
                  )
                }
              >
                <Save20Regular style={{ fontSize: 16 }} /> Speichern
              </button>
              <button
                type="button"
                className={styles.secondaryBtn}
                disabled={!canWrite || working || dirty}
                onClick={() =>
                  void runRowAction(
                    r.id,
                    () => markNamenszusatzPasstSo(r.id),
                    `„${bisher}“ bleibt unverändert und ist abgehakt.`,
                  )
                }
              >
                <CheckmarkCircle20Regular style={{ fontSize: 16 }} /> Passt so
              </button>
              <span className={styles.hint}>
                {dirty
                  ? "Speichern übernimmt die Änderung und hakt den Kontakt ab."
                  : '„Passt so“ lässt alles unverändert und hakt den Kontakt ab.'}
              </span>
            </>
          )}
          {working && <Spinner size="tiny" />}
          {err && <span className={styles.rowError}>{err}</span>}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.root}>
      <button className={styles.backBtn} onClick={goBack}>
        <ArrowLeft20Regular /> Zurück zur Datenbereinigung
      </button>

      <div className={styles.bar}>
        <div className={styles.titleRow}>
          <span className={styles.titleIcon}>
            <TextboxAlignBottomRotate9020Regular style={{ fontSize: 22 }} />
          </span>
          <h1 className={styles.title}>Namenszusätze bereinigen</h1>
        </div>
        <div className={styles.goldLine} />
        <span className={styles.subtitle}>
          Kontakte mit einem Namenszusatz aus der Datenübernahme: entweder die
          Fortsetzung eines abgeschnittenen Namens („AGGK Aachener Glas-" +
          „verarbeitungsgesellschaft mbH") oder eine Vertretungsangabe („v. d.
          Herff & Herff Verw."). Name, Vorname und Zusatz prüfen — korrigiert
          speichern oder unverändert abhaken.
        </span>
      </div>

      <div className={styles.filterBar}>
        <button
          className={mergeClasses(styles.pill, (status === "open") && styles.pillActive)}
          onClick={() => setStatus("open")}
        >
          Offen ({counts.open})
        </button>
        <button
          className={mergeClasses(styles.pill, (status === "erledigt") && styles.pillActive)}
          onClick={() => setStatus("erledigt")}
        >
          Erledigt ({counts.erledigt})
        </button>
        {teams.length > 0 && (
          <select
            className={styles.select}
            value={teamId}
            onChange={(e) => setTeamId(e.target.value)}
            title="Nach Verantwortlichkeit (BO-Team) filtern"
            aria-label="Nach Verantwortlichkeit filtern"
          >
            <option value="">Alle Verantwortlichen</option>
            <option value="none">Ohne Verantwortlichkeit</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name ?? t.id}
              </option>
            ))}
          </select>
        )}
        <div className={styles.searchRow}>
          <Search20Regular style={{ color: BRAND.muted, flexShrink: 0 }} />
          <input
            className={styles.searchInput}
            type="search"
            placeholder="Name, Zusatz oder Kundennummer suchen …"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {q && (
            <button
              className={styles.clearBtn}
              onClick={() => setQ("")}
              aria-label="Suche zurücksetzen"
            >
              <Dismiss16Regular />
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className={styles.errorBox}>
          <Warning20Regular /> {error}
        </div>
      )}
      {!canWrite && (
        <span className={styles.hint}>
          Nur Leserechte — zum Bearbeiten wird die Berechtigung „Kontakte
          bearbeiten" gebraucht.
        </span>
      )}

      {flash && (
        <div className={styles.rowOk}>
          <CheckmarkCircle20Regular style={{ fontSize: 16 }} /> {flash}
        </div>
      )}

      {/* Eigener Scroll-Container: die App-Shell scrollt nicht mit dem Fenster,
          ohne `scrollableTarget` lädt InfiniteScroll nie nach. `dvh` statt `vh`
          wegen der Adressleiste auf Tablets/Handys. */}
      <div
        id="scrollableNamenszusatz"
        style={{ height: "calc(100dvh - 340px)", minHeight: "260px", overflowY: "auto" }}
      >
        {loading ? (
          <p className={styles.empty}>Lade Kontakte…</p>
        ) : rows.length === 0 ? (
          <p className={styles.empty}>
            {qDebounced
              ? "Kein Kontakt passt zur Suche."
              : teamId
                ? "Kein Kontakt mit dieser Verantwortlichkeit."
                : status === "open"
                  ? "Alle Namenszusätze sind geprüft."
                  : "Noch nichts abgehakt."}
          </p>
        ) : (
          <InfiniteScroll
            dataLength={rows.length}
            next={fetchMore}
            hasMore={hasMore}
            loader={<div className={styles.loadMore}>Lade weitere Kontakte…</div>}
            scrollableTarget="scrollableNamenszusatz"
            style={{ overflow: "visible" }}
          >
            {rows.map((r) => renderCard(r))}
          </InfiniteScroll>
        )}
      </div>

      {total > 0 && (
        <span className={styles.hint}>
          {rows.length} von {total} angezeigt
        </span>
      )}
    </div>
  );
};

export default NamenszusatzCleanupPage;
